import ReceptionCoverSummary from './ReceptionCoverSummary';
import ReceptionNoticePanel from './ReceptionNoticePanel';
import ReceptionCoverPanel from './ReceptionCoverPanel';
import { bookingChannelLabel } from '@/dashboard/appointments/ClinicDayBoard';
import { collectionOutstandingMinorUnits } from '@/dashboard/billing/checkout-handoff-view';
import type { BillingDocumentPreview } from '@/dashboard/billing/PatientBillingWorkspace';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import { useCallback, useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import './walkin.css';
import { useAuth } from '@/auth/auth-context';
import { fetchWalkinWorkstation, fetchTodayAppointments, fetchPatientMasterSearch, fetchCheckoutHandoff } from '@/dashboard/api';
import RescheduleAppointmentDialog from '@/dashboard/appointments/RescheduleAppointmentDialog';
import { allowedWorkbenchActions } from '@/dashboard/owner/AppointmentWorkbench';
import ClinicDayBoard, {
  canSeeDayBoard,
  canAssignDoctorOnRow,
  receptionDoctorAssignmentLocked,
  dayBoardPatientLabel,
  dayBoardNextAction,
  dayBoardActionLabel,
  currentVisitStatusChip,
  dayBoardViewId,
  dayBoardViewRows,
  dayRowOutcomeSignals,
  dayStateCounts,
  deskActionCounts,
  nextExpectedRowId,
  opSoFarCount,
  returningVisitCount,
  receptionDaySummary,
  DAY_BOARD_VIEWS,
  DAY_BOARD_VIEW_ONLY_COPY,
  DAY_VIEW_CHECKED_OUT_EMPTY_COPY,
  canActOnDayBoard,
  type DayBoardActionKind,
} from '@/dashboard/appointments/ClinicDayBoard';
import {
  collectQueueSummary,
  derivePaymentHandoff,
  visitClosedForHandoff,
  NO_HANDOFF_ADVISED_ONLY_COPY,
} from '@/dashboard/appointments/payment-handoff';
import { intakeRowPlan } from '@/dashboard/appointments/intake-next-action';
import { checkoutHandoff } from '@/dashboard/appointments/workflow-handoff';
import { useClinicLens, clinicOptionLabel } from '@/dashboard/clinic-context';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  AcButton,
  ClinicScopeLabel,
  DateCommandBar,
  DaySummaryRibbon,
  PageHeader,
  ViewChips,
  dayScopeHeading,
  dayScopeReadOnlyCopy,
  dayScopeRequest,
  useDayScope,
} from '@/dashboard/ui';
import DateField from '@/dashboard/ui/DateField';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import PaymentDrawer from '@/dashboard/billing/PaymentDrawer';
import { WorkspaceSurfaceContext } from '@/dashboard/ui/WorkspaceSurfaceContext';
import MasterDetail, { DetailAside, WorkPanel } from '@/dashboard/ui/MasterDetail';
import GlobalPatientSearchModal, {walkinBoardHref} from '@/dashboard/patients/GlobalPatientSearchModal';
import ReceptionSearch from '@/dashboard/patients/ReceptionSearch';
import PatientRecordPage from '@/dashboard/patients/PatientRecordPage';
import PatientQuickDetails, { QUICK_DETAIL_LABELS, readQuickDetail, type QuickDetail } from '@/dashboard/patients/PatientQuickDetails';
import RoleHomeSearchPortal from '@/dashboard/ui/RoleHomeSearchPortal';
import { canUseLeadBridge } from '@/dashboard/patients/patient-master-view';
import { resolveCancelReasonCodes } from '@/dashboard/appointments/cancel-reasons';
import type {
  ClinicRosterDoctor,
  TodayAppointment,
  WorkstationData,
  WorkstationRecentWalkin,
} from '@/dashboard/types';
import WalkinActivityPanel from './WalkinActivityPanel';
import WalkinFollowupsPanel from './WalkinFollowupsPanel';
import WalkinSourceCorrectionDoor from './WalkinSourceCorrectionDoor';
import { clinicDateLabel, clinicToday, isClinicDate, resolveDayScope, stepClinicDate, writeDayParam } from '@/dashboard/ui/day-scope';
import { formatIstTime } from '@/lib/datetime-ist';

/**
 * AdoraCare - /walkin entry point, the front-desk HOME
 * (S2-P5C dashboard redesign; live panels in S2-P5E-B1; the "Today" command
 * home in S1-P2-RECEPTIONIST-HOME-TODAY-COMMAND; recomposed to HOME by
 * FRONT-OFFICE-OS-V2-BUILD-V1, founder rulings FO-2/FO-3 2026-08-01).
 *
 * /walkin renders INSIDE the same shell as the dashboard pages: App.tsx nests
 * this route under DashboardLayout, so the top header and the left nav are
 * present. The role-scoped `me` payload comes from the DashboardMeProvider that
 * DashboardLayout already supplies (no duplicate /me fetch); the live panels
 * read the receptionist workstation feed (Recent walk-ins) and the standalone
 * appointments-today read (the day board).
 *
 * Layout hierarchy (FO-2/FO-3): the landing page is HOME -- the GLOBAL
 * SEARCH box (mobile / UMR / name) is the hero and THE door to every person
 * (search-first, C13: results and registration live in the centered modal,
 * GlobalPatientSearchModal); today's DAY BOARD is the primary content below
 * it. The standalone Register-walk-in block is REMOVED from this home --
 * search IS the door (RECEPTION-OS-BUILD-V1 F-13: the unhosted
 * WalkinWorkflow family is now DEMOLISHED per the audit row; registration
 * lives in the modal + arrival wizard only). FO-8: a multi-clinic operator
 * sees the active clinic named in the header (the global switcher stays
 * the scoping authority).
 *
 * The active clinic is resolved from SESSION SCOPE (locked decision - there is
 * no clinic field in the body): the workstation's backend-resolved
 * `activeClinicId`, falling back to the first clinic in the user's scope. The
 * staff identity is never faked here - the backend records the actor from the
 * bearer.
 *
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (founder ruling 2026-08-04): Home is not
 * a summary page with links to the real work -- it IS the operating surface
 * for the SELECTED clinic day. The DAY REGION below (the DATE COMMAND BAR, the
 * glance strip and the day board) re-renders IN PLACE when the day changes;
 * there is no navigation, no second screen, and no second day surface. The
 * adoption is READ-SIDE ONLY: every check-in, checkpoint, registration,
 * payment and assignment path in this file and below it is byte-untouched, and
 * on TODAY the whole page renders exactly as it did before this package.
 * Off today the board's ONE day-scope lens withholds actions and live clocks
 * (see ClinicDayBoardViewProps.dayScope) -- the desk reads recorded truth for
 * a past day and the plan for a future one.
 */

/**
 * FRONT-OFFICE-DENSITY-PASS-V1 (D2): the board row's OWN Full-billing ghost link
 * is RETIRED. The deep-work fallback is not deleted -- it moved one layer in, to
 * the shipped drawer that already carries it
 * (PaymentDrawer.PAYMENT_DRAWER_FULL_BILLING_COPY + its fullBillingHref, wired
 * below), so a money row renders ONE payment control instead of two and there is
 * ONE Full-billing copy owner instead of two. The navigate-only path (no
 * onCollect) never rendered a second control and is byte-identical.
 */

/**
 * The receptionist's per-row payment door (HANDOFF LAW; exported for direct
 * pinning). ONE derivation decides the shape:
 *   collect       -> the LOUD primary Collect CTA carrying the suggested sum
 *                    into the billing prefill (&suggestedAmount, editable);
 *   advised_only  -> the quiet no-handoff line (never a button);
 *   none (closed, plan-less, unpaid) -> the original quiet Record-payment
 *                    door, byte-identical;
 *   recorded / pre-close -> nothing.
 */
export function ReceptionistPaymentDoor({
  row,
  activeClinicId,
  onCollect,
  collectDisabled = false,
}: {
  row: TodayAppointment;
  activeClinicId: number | null;
  /**
   * SMART-PAYMENT-DRAWER-V1 (Refs #315): when supplied, the payment door
   * opens the in-place collect drawer instead of NAVIGATING, and the
   * billing-entry href stays beside it as the explicit deep-work fallback
   * (history, voids, split, complex cases). ABSENT = today's navigation
   * behaviour, byte-identical -- every pre-package pin renders unchanged.
   */
  onCollect?: (appointmentId: number) => void;
  collectDisabled?: boolean;
}): JSX.Element | null {
  const handoff = derivePaymentHandoff(row);
  // Home and its quick view share the verified-balance navigation gate.
  const step = checkoutHandoff(row);
  // Convert verified minor units only for the existing whole-rupee prefill.
  const due = row.outstandingMinorUnits;
  const suggestedAmount = step !== null && typeof due === 'number' && due % 100 === 0 ? due / 100 : null;
  if (onCollect !== undefined && step === null) return null;
  const href = `/dashboard/billing-entry?appointmentId=${row.id}${
    typeof row.patientId === 'string' && row.patientId !== ''
      ? `&patientId=${encodeURIComponent(row.patientId)}`
      : ''
  }${activeClinicId !== null ? `&clinicId=${activeClinicId}` : ''}`;
  if (step !== null) {
    const collectHref = `${href}${
      suggestedAmount !== null ? `&suggestedAmount=${suggestedAmount}` : ''
    }`;
    if (onCollect !== undefined) {
      // D2: ONE payment control on the row -- the drawer carries the
      // Full-billing fallback (and the collectHref's prefill) itself.
      return (
        <AcButton
          variant="primary"
          size="sm"
          title={suggestedAmount === null ? 'Collect payment' : `Collect payment · ${formatMinorUnitsInr(suggestedAmount * 100)} suggested`}
          aria-label={suggestedAmount === null ? 'Collect payment' : `Collect payment · ${formatMinorUnitsInr(suggestedAmount * 100)} suggested`}
          data-testid={`day-board-collect-${row.id}`}
          disabled={collectDisabled}
          onClick={() => onCollect(row.id)}
        >
          Collect payment
        </AcButton>
      );
    }
    return (
      <Link
        to={collectHref}
        className="ac-btn ac-btn--primary ac-btn--sm"
        title="From the saved visit balance"
        data-testid={`day-board-collect-${row.id}`}
      >
        {step.label}
      </Link>
    );
  }
  if (handoff.kind === 'advised_only') {
    return (
      <span
        className="day-board__row-hint"
        data-testid={`day-board-no-handoff-${row.id}`}
      >
        {NO_HANDOFF_ADVISED_ONLY_COPY}
      </span>
    );
  }
  // MONEY-COMPLETION-V1 (rider R-52, the proven read-side hole): an
  // advised-EVENT-only closed visit (no plan-item rows on the wire)
  // derives handoff 'none' -- but the plan-count twin of the SAME clinical
  // state renders the no-handoff line above, so the event-signal twin must
  // too (one vocabulary; THE one board-row signals call), instead of
  // falling through to the quiet generic door (erin's shape). The gate
  // MIRRORS the chip's exactly (adversarial review S2): the #247 closed
  // predicate -- which includes the R-1 ungranted checked_in+finalized
  // branch -- plus the terminal-negative pair, plus not-recorded (an
  // older wire's undefined paymentRecorded stays honest here too).
  if (
    row.paymentRecorded !== true &&
    visitClosedForHandoff(row) &&
    row.status !== 'cancelled' &&
    row.status !== 'no_show' &&
    dayRowOutcomeSignals(row) === 'awaiting_decision'
  ) {
    return (
      <span
        className="day-board__row-hint"
        data-testid={`day-board-no-handoff-${row.id}`}
      >
        {NO_HANDOFF_ADVISED_ONLY_COPY}
      </span>
    );
  }
  // The original quiet Record-payment door, byte-identical for
  // signal-less closed rows.
  if (
    (row.status === 'fulfilled' || row.status === 'completed') &&
    row.paymentRecorded === false
  ) {
    if (onCollect !== undefined) {
      // D2: ONE payment control (see the collect branch above).
      return (
        <AcButton
          variant="secondary"
          size="sm"
          data-testid={`day-board-payment-${row.id}`}
          disabled={collectDisabled}
          onClick={() => onCollect(row.id)}
        >
          Record payment
        </AcButton>
      );
    }
    return (
      <Link
        to={href}
        className="ac-btn ac-btn--secondary ac-btn--sm"
        data-testid={`day-board-payment-${row.id}`}
      >
        Record payment
      </Link>
    );
  }
  return null;
}

type WorkstationState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: WorkstationData }
  | { kind: 'error' };

type AppointmentsState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      data: TodayAppointment[];
      /** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the clinic's ACTIVE
       *  roster off the SAME board read (assign-picker input). */
      clinicDoctors: readonly ClinicRosterDoctor[];
      /** X27 (Lane 3, 2026-09-10): the SAME board read's day-level
       *  contactPointId-lookup-degraded flag (absent on an older backend
       *  or the non-board view -- defaults to false, "not degraded"). */
      contactPointLookupDegraded: boolean;
    }
  | { kind: 'error' };

export default function WalkinApp(): JSX.Element {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const meCtx = useDashboardMeContext();
  const [ws, setWs] = useState<WorkstationState>({ kind: 'loading' });
  const [apptsRead, setAppts] = useState<{ scopeKey: string | null; state: AppointmentsState }>({
    scopeKey: null, state: { kind: 'loading' },
  });
  // FRONT-OFFICE-DENSITY-PASS-V1 (D3): the clock the waiting-time line is
  // measured against, stamped when the day READ resolves -- so "Waiting 12 min"
  // is as-of the same moment as every other number on this page (the tiles, the
  // counts, the rows), never a render-time value drifting away from stale rows.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  /*
   * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1: THE SELECTED CLINIC DAY. The hook is
   * the ONE owner of the day, its URL persistence (refresh-safe, shareable,
   * back / forward honest), the past / today / future verdict and the
   * request-identity gate. Default = today, so an operator who never touches
   * the bar sees exactly the page they saw before this package.
   */
  const day = useDayScope();
  // SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T3, founder ruling 2026-08-15): the
  // exact-appointment landing -- an id search's door arrives here as
  // ?appointment=<id> (walkinBoardHref), and the board scrolls to +
  // highlights THAT row. Read-only presentation state: an absent/junk param
  // is null and the board is byte-identical.
  const [searchParams, setSearchParams] = useSearchParams();
  // Reception Home is one selected day (Ravi, 13 October); legacy range URLs cannot change its scope.
  const rangeEnd = meCtx?.me?.role === 'receptionist' ? undefined : searchParams.get('endDate') || undefined;
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [rangeStartDraft, setRangeStartDraft] = useState(day.date);
  const [rangeEndDraft, setRangeEndDraft] = useState(day.date);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const selectAppointmentDate = (date: string): void => {
    if (workSurfaceOpen || !isClinicDate(date)) return;
    const params = new URLSearchParams(writeDayParam([...searchParams.entries()], date, day.today));
    params.delete('endDate');
    setSearchParams(params);
  };
  const highlightAppointmentId = (() => {
    const raw = searchParams.get('appointment');
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
  })();
  // FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "CHECK OUT PATIENTS"): WHICH day
  // view the board table renders -- the whole day, or the desk-closed
  // (checked-out) rows only. URL-carried (?view=checked_out; C-9: the URL is
  // the canonical view state, the useDayScope precedent on this same page);
  // an absent/junk token is the full day (fail-open to the honest whole).
  // A CUT over the SAME fetched rows -- zero second reads; the head counts
  // below keep describing the WHOLE day.
  const dayView = dayBoardViewId(searchParams.get('view'));
  const selectDayView = useCallback(
    (id: string) => {
      const params = new URLSearchParams(searchParams);
      if (id === 'checked_out') params.set('view', 'checked_out');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  // The ViewChips copy-URL door (the LeadsWorkspacePage idiom verbatim).
  const [viewUrlCopied, setViewUrlCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  const onCopyViewUrl = useCallback((): void => {
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : null;
      if (nav?.clipboard?.writeText !== undefined && typeof window !== 'undefined') {
        void nav.clipboard.writeText(window.location.href);
      }
      setViewUrlCopied(true);
      if (copyTimer.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(copyTimer.current);
      }
      if (typeof window !== 'undefined') {
        copyTimer.current = window.setTimeout(() => setViewUrlCopied(false), 1600);
      }
    } catch {
      // Clipboard denied: the URL bar itself remains the bookmarkable truth.
    }
  }, []);

  useEffect(() => {
    document.title = 'Home - AdoraCare';
  }, []);
  // FO-3: the hero search box's typed query + the centered modal it opens.
  // The QUERY lives here (survives the modal closing); the modal owns the
  // results, the family list, and the in-modal register flow.
  const [heroQuery, setHeroQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchPanelTarget,setSearchPanelTarget]=useState<HTMLDivElement|null>(null);
  const [searchPanelOpen,setSearchPanelOpen]=useState(false);
  const searchReturnQuickId = useRef<number | null>(null);

  // FRONT-OFFICE-DAY-BOARD-TRUTH-AND-WAIT-CLOCK-V1: advance the ONE injected
  // clock once a MINUTE (never per second, never per render).
  //
  // WHY THIS EXISTS: nowMs was stamped only when the day read resolved, and
  // the page never polls -- so on an idle board every elapsed value froze at
  // its read-time value while real minutes passed. A clock that says "5m"
  // ninety minutes later is a WRONG clock, and a wrong clock is worse than
  // no clock. Elapsed time is the ONLY now-relative fact on this page; every
  // other number (the counts, the tiles, the row states) is a snapshot of the
  // read and is untouched by this tick, so the D3 coherence intent holds
  // where it is meaningful and the two duration facts stop lying. The
  // interval is the sole change to the D3 waiting line: same derivation, same
  // gate, same copy -- only minute-accurate instead of frozen.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const me = meCtx?.me ?? null;
  // A signed-in session whose /me resolved (me present) drives the board. The
  // live feeds load only then.
  const hasMe = me !== null;
  const clinicIds = me?.clinicIds ?? [];

  const wsData = ws.kind === 'ready' ? ws.data : null;
  const wsAvailable = ws.kind === 'ready';

  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2).
  // This surface reads the lens and does NOT re-derive scope. The old gate
  // re-validated the pick against `me.clinicIds` -- the REACH list -- which is
  // empty for an owner (owner reach expands from the clinics table), so an
  // owner's pick was silently dropped and this page showed a different clinic
  // than the header said. The lens has ALREADY validated its selection against
  // the verified options it was built from (deriveClinicContext fails closed on
  // an out-of-scope stored id), so re-checking here can only ever be wrong.
  const clinicCtx = useClinicLens();
  const globalPick = clinicCtx?.selectedClinicId ?? null;
  const activeClinicId =
    globalPick ?? wsData?.activeClinicId ?? (clinicIds.length > 0 ? clinicIds[0] : null);

  const loadWorkstation = useCallback(() => {
    let cancelled = false;
    setWs({ kind: 'loading' });
    fetchWalkinWorkstation()
      .then((data) => {
        if (!cancelled) setWs({ kind: 'ready', data });
      })
      .catch(() => {
        // The workstation feed is always live (S2-P5E-B1 removed the
        // dark-by-default gate). Any fetch failure degrades the live counts to
        // an honest "unavailable" state in the panels rather than inventing
        // zeros - never a fabricated number.
        if (!cancelled) setWs({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only load the workstation feed once /me has resolved (it carries the live
  // counts and the active-clinic resolution). Re-runs if the user changes.
  useEffect(() => {
    if (!hasMe) {
      setWs({ kind: 'loading' });
      return;
    }
    return loadWorkstation();
  }, [hasMe, loadWorkstation]);

  // TRAIN-B: board-audience roles read the SAME endpoint widened to the day
  // BOARD (full-day statuses + ops-status); other roles (call_center_agent)
  // keep the original upcoming view - the server 401s ?view=board for them,
  // so this mirror never requests it (canSeeDayBoard).
  const boardMode = canSeeDayBoard(me?.role ?? null);
  // A refresh may retain verified rows only for the same audience and day.
  // Derive this during render so a scope change cannot briefly paint old rows.
  const appointmentsScope = JSON.stringify([
    user?.id ?? null, me?.organizationId ?? null, me?.role ?? null,
    [...clinicIds].sort((a, b) => a - b), activeClinicId, boardMode, day.date, rangeEnd ?? null,
  ]);
  const appts: AppointmentsState = apptsRead.scopeKey === appointmentsScope
    ? apptsRead.state : { kind: 'loading' };

  // Today's plan / day board: the standalone appointments-today read for the
  // active clinic. Keyed on activeClinicId so it refreshes once the
  // workstation resolves the clinic. A null clinic (none in the user's scope)
  // is an honest empty - not a fetch and never invented rows. Independent of
  // the workstation feed (founder decision a): an appointments error does not
  // blank the walk-in counts. `apptsReloadKey` refetches in place after a
  // day-board action (the board's onChanged).
  const [apptsReloadKey, setApptsReloadKey] = useState(0);
  const reloadAppointments = useCallback(() => {
    setApptsReloadKey((k) => k + 1);
  }, []);
  // SMART-PAYMENT-DRAWER-V1: the in-place collect drawer's armed episode.
  // null = closed. Opening it changes NO url -- the day board stays put.
  const [collectAppointmentId, setCollectAppointmentId] = useState<number | null>(
    null,
  );
  const [utilityPanel, setUtilityPanel] = useState<'tasks' | 'registrations' | null>(null);
  const [noticeAppointment,setNoticeAppointment]=useState<number|null>(null);
  // Retain only an explicitly selected visit while opening a cover task.
  const [coverSelection,setCoverSelection]=useState<{clinicId:number;date:string;appointmentId:number}|null>(null);
  const [coverMode,setCoverMode] = useState<'report'|'resolve'|null>(null);
  const coverTrigger=useRef<HTMLElement|null>(null);
  useEffect(()=>{setCoverMode(null);setNoticeAppointment(null);},[activeClinicId]);
  const [taskCount, setTaskCount] = useState<number | null>(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const [totalsOpen, setTotalsOpen] = useState(false);
  const [finishedOpen, setFinishedOpen] = useState(false);
  const [visitLead, setVisitLead] = useState<{ leadId: number; title: string } | null>(null);
  const quickDetail = readQuickDetail(searchParams.get('quickDetail'));
  const quickAppointmentId = Number(searchParams.get('quickAppointment'));
  const quickOpen = me?.role === 'receptionist' && Number.isSafeInteger(quickAppointmentId) && quickAppointmentId > 0;
  useEffect(()=>{setCoverSelection(null);setNoticeAppointment(null);setCoverMode(null);},[activeClinicId,day.date,user?.id,me?.role]);
  const documentHostKey = JSON.stringify([user?.id,me?.organizationId,me?.role,activeClinicId,quickAppointmentId,quickDetail,day.date,searchParams.get('billingDestination'),searchParams.get('quickDocument')]);
  const [documentPreview,setDocumentPreview] = useState<{key:string;value:BillingDocumentPreview}|null>(null);
  const onDocumentPreviewChange = useCallback((value:BillingDocumentPreview) => {
    setDocumentPreview(previous => {
      if(value === null) return previous?.key === documentHostKey ? null : previous;
      if(previous?.key === documentHostKey && previous.value?.kind === value.kind && previous.value.id === value.id) return previous;
      return {key:documentHostKey,value};
    });
  }, [documentHostKey]);
  const correctionReceiptPreview = quickOpen && quickDetail === 'bill' && documentPreview?.key === documentHostKey && documentPreview.value?.kind === 'receipt';
  const recordPatientId = searchParams.get('recordPatient');
  const recordAppointmentId = Number(searchParams.get('recordAppointment'));
  const recordOpen = me?.role === 'receptionist' && me.patientMasterEnabled === true &&
    recordPatientId !== null && recordPatientId !== '' &&
    Number.isSafeInteger(recordAppointmentId) && recordAppointmentId > 0;
  const recordTrigger = useRef<HTMLElement | null>(null);
  const recordReturnId = useRef<number | null>(null);
  const quickViewScroll = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!quickOpen || !quickViewScroll.current) return;
    const position = quickViewScroll.current;
    quickViewScroll.current = null;
    // WorkPanel owns keyboard focus. Keep its focus transfer from moving the
    // surrounding Home page when the selected row reflows beside the dock.
    const frame = requestAnimationFrame(() => {
      window.scrollTo(position.x, position.y);
      // The reference opens the reflowed queue from its top; a retained pixel
      // offset from the wide table otherwise starts midway through a card.
      const list = document.querySelector<HTMLElement>('[data-testid="day-board-table-scroll"]');
      if (list) list.scrollTop = 0;
    });
    return () => cancelAnimationFrame(frame);
  }, [quickOpen, quickAppointmentId]);
  const [recordDoorBusy, setRecordDoorBusy] = useState(false);
  const [recordDoorError, setRecordDoorError] = useState<string | null>(null);
  const quickActionSerial = useRef(0);
  const [rescheduleRow, setRescheduleRow] = useState<TodayAppointment | null>(null);
  useEffect(() => { setRescheduleRow(null); }, [activeClinicId, quickAppointmentId, day.date]);
  const rescheduleReturn = useRef<HTMLElement | null>(null);
  const closeReschedule = () => {
    setRescheduleRow(null);
    requestAnimationFrame(() => {
      const target=rescheduleReturn.current?.isConnected?rescheduleReturn.current:document.querySelector<HTMLElement>('[data-testid="home-quick-reschedule"]');
      const menu=target?.closest('details');
      if(menu) menu.open=true;
      target?.focus();
    });
  };
  const [quickRequestedAction, setQuickRequestedAction] = useState<{
    appointmentId: number; action: DayBoardActionKind | 'intake' | 'assign_doctor'; requestId: number;
  } | null>(null);
  const recordDoorAttempt = useRef(0);
  const recordDoorMounted = useRef(true);
  const recordDoorContext = useRef({
    key: location.key,
    pathname: location.pathname,
    search: location.search,
    actorId: user?.id ?? null,
    role: me?.role ?? null,
    patientMasterEnabled: me?.patientMasterEnabled === true,
    clinicId: activeClinicId,
    appointmentsVersion: apptsReloadKey,
  });
  recordDoorContext.current = {
    key: location.key,
    pathname: location.pathname,
    search: location.search,
    actorId: user?.id ?? null,
    role: me?.role ?? null,
    patientMasterEnabled: me?.patientMasterEnabled === true,
    clinicId: activeClinicId,
    appointmentsVersion: apptsReloadKey,
  };
  useEffect(() => {
    recordDoorMounted.current = true;
    return () => {
      recordDoorMounted.current = false;
      recordDoorAttempt.current += 1;
    };
  }, []);
  const recordDoorScopeKey = [location.key, user?.id, me?.role, me?.patientMasterEnabled, activeClinicId, apptsReloadKey].join('|');
  useEffect(() => {
    // Date/view, clinic, actor, or a refreshed board invalidates an unresolved
    // lookup. Its eventual response must not reopen an older visit.
    recordDoorAttempt.current += 1;
    setRecordDoorBusy(false);
    setRecordDoorError(null);
  }, [recordDoorScopeKey]);
  const openPatientQuickView = (row: TodayAppointment): void => {
    quickViewScroll.current = { x: window.scrollX, y: window.scrollY };
    setCoverSelection(null);
    recordTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const params = new URLSearchParams(searchParams);
    params.set('quickAppointment', String(row.id));
    params.delete('quickDetail'); params.delete('quickVisit'); params.delete('quickDocument'); params.delete('billingDestination');
    setSearchParams(params);
  };
  const closePatientQuickView = (restoreFocus = true): void => {
    recordReturnId.current = restoreFocus ? quickAppointmentId : null;
    const params = new URLSearchParams(searchParams);
    params.delete('quickAppointment');
    params.delete('quickDetail'); params.delete('quickVisit'); params.delete('quickDocument'); params.delete('billingDestination');
    setSearchParams(params);
  };
  // Preserve deep-link/reload state, but never carry a billing destination across clinic/visit changes.
  const billingScope = `${activeClinicId}:${quickAppointmentId}`;
  const priorBillingScope = useRef(billingScope);
  const billingScopeReady = useRef(activeClinicId !== null);
  useEffect(() => {
    if (!billingScopeReady.current) {
      priorBillingScope.current = billingScope; billingScopeReady.current = activeClinicId !== null; return;
    }
    if (priorBillingScope.current === billingScope) return;
    priorBillingScope.current = billingScope;
    const next = new URLSearchParams(searchParams);
    if (!next.has('billingDestination') && !next.has('quickDocument')) return;
    next.delete('billingDestination'); next.delete('quickDocument'); setSearchParams(next, {replace: true});
  }, [activeClinicId, billingScope, searchParams, setSearchParams]);
  const quickDetailReturn = useRef<QuickDetail | null>(null);
  const closeQuickDetail = (): void => {
    quickDetailReturn.current = quickDetail;
    const params = new URLSearchParams(searchParams);
    params.delete('quickDetail'); params.delete('quickVisit'); params.delete('quickDocument'); params.delete('billingDestination');
    setSearchParams(params);
  };
  useEffect(() => {
    if (quickDetail !== null || quickDetailReturn.current === null || !quickOpen || recordOpen || appts.kind !== 'ready') return;
    const frame = requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(`[data-testid="home-quick-${quickDetailReturn.current}"]`);
      if (target?.isConnected) {
        const disclosure = target.closest('details');
        if (disclosure) disclosure.open = true;
        target.focus(); quickDetailReturn.current = null;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [quickDetail, quickOpen, recordOpen, appts.kind]);
  const openQuickDetail = (detail: QuickDetail): void => {
    const params = new URLSearchParams(searchParams); params.set('quickDetail', detail);
    params.delete('quickVisit'); params.delete('quickDocument'); params.delete('billingDestination'); setSearchParams(params);
  };
  const openPatientRecord = async (row: TodayAppointment): Promise<void> => {
    if (me?.role !== 'receptionist' || me.patientMasterEnabled !== true) return;
    // The board's patientId can be lead-derived for billing. Only its
    // appointment-level record signal authorizes this exact-visit door.
    if (row.hasPatientRecord !== true) return;
    const attempt = ++recordDoorAttempt.current;
    const context = recordDoorContext.current;
    const current = (): boolean => {
      const latest = recordDoorContext.current;
      return recordDoorMounted.current && recordDoorAttempt.current === attempt &&
        latest.key === context.key && latest.pathname === context.pathname && latest.search === context.search &&
        latest.actorId === context.actorId && latest.role === 'receptionist' && latest.patientMasterEnabled &&
        latest.clinicId === context.clinicId && latest.appointmentsVersion === context.appointmentsVersion &&
        window.location.pathname === context.pathname && window.location.search === context.search;
    };
    recordTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setRecordDoorError(null);
    setRecordDoorBusy(true);
    let patientId = row.patientId;
    const umr = row.linkedPatientUmr?.trim();
    if (umr) {
      try {
        // The board's patientId is lead-derived for billing, while its linked
        // UMR follows the direct patient link first. Resolve that server-shipped
        // identity through the existing scoped exact search, even when the
        // legacy patientId also exists; never infer identity from a name.
        const result = await fetchPatientMasterSearch(umr, 'umr');
        if (!current()) return;
        const matches = result.candidates.filter((candidate) =>
          candidate.umr.toUpperCase() === umr.toUpperCase(),
        );
        if (matches.length !== 1) throw new Error('Patient record could not be verified. Search by UMR.');
        patientId = matches[0]!.patientId;
      } catch {
        if (current()) {
          setRecordDoorError('Patient record could not be verified. Search by UMR.');
          setRecordDoorBusy(false);
        }
        return;
      }
    }
    if (!current()) return;
    if (!patientId) {
      setRecordDoorError('Patient record could not be verified. Search by UMR.');
      setRecordDoorBusy(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('recordPatient', patientId);
    params.set('recordAppointment', String(row.id));
    setSearchParams(params);
    if (recordDoorAttempt.current === attempt && recordDoorMounted.current) setRecordDoorBusy(false);
  };
  const closePatientRecord = (): void => {
    recordReturnId.current = recordAppointmentId;
    const params = new URLSearchParams(searchParams);
    params.delete('recordPatient');
    params.delete('recordAppointment');
    setSearchParams(params);
  };
  useEffect(() => {
    // After reload the board may still be loading when the record closes. Wait
    // for the row to be re-enabled, then restore focus to its actual trigger.
    if (recordOpen || quickOpen || recordReturnId.current === null || appts.kind !== 'ready') return;
    const rowId = recordReturnId.current;
    const frame = requestAnimationFrame(() => {
      const target = recordTrigger.current?.isConnected && !recordTrigger.current.hasAttribute('disabled')
        ? recordTrigger.current
        : document.querySelector<HTMLElement>(`[data-testid="day-board-open-${rowId}"]`) ??
          document.querySelector<HTMLElement>('[data-testid="home-global-search-input"]');
      if (target?.isConnected && !target.hasAttribute('disabled')) {
        target.focus();
        recordReturnId.current = null;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [recordOpen, quickOpen, appts.kind]);
  useEffect(() => {
    // Search close refreshes the board, replacing the quick-view trigger. Wait
    // for that read and restore its logical identity, not a detached element.
    if (searchOpen || searchReturnQuickId.current === null || appts.kind !== 'ready') return;
    const frame = requestAnimationFrame(() => {
      const target = quickOpen && quickAppointmentId === searchReturnQuickId.current
        ? document.querySelector<HTMLElement>('[data-testid="home-quick-book"]')
        : document.querySelector<HTMLElement>('[data-testid="home-global-search-input"]');
      if (target?.isConnected && !target.hasAttribute('disabled')) {
        const disclosure = target.closest('details');
        if (disclosure) disclosure.open = true;
        target.focus();
        searchReturnQuickId.current = null;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [searchOpen, appts.kind, quickOpen, quickAppointmentId]);
  const utilityTrigger = useRef<HTMLElement | null>(null);
  const visitTrigger = useRef<HTMLElement | null>(null);
  const [visitBusy, setVisitBusy] = useState(false);
  const onTaskSummary = useCallback((count: number | null, busy: boolean) => {
    setTaskCount(count); setTaskBusy(busy);
  }, []);
  const closeUtility = (): void => {
    if (taskBusy) return;
    setUtilityPanel(null);
    utilityTrigger.current?.focus();
  };
  const openVisit = (leadId: number, title: string): void => {
    visitTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setVisitLead({ leadId, title });
  };
  const closeVisit = (): void => {
    if (visitBusy) return;
    setVisitLead(null);
    requestAnimationFrame(() => visitTrigger.current?.focus());
  };
  const [workSurfaceTarget, setWorkSurfaceTarget] = useState<HTMLDivElement | null>(null);
  const [workSurfaceOpen, setWorkSurfaceOpen] = useState(false);
  const onWorkSurfaceChange = useCallback((open: boolean) => setWorkSurfaceOpen(open), []);
  const [contactPanelTarget, setContactPanelTarget] = useState<HTMLDivElement | null>(null);
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  // Stable identity matters: a contact control uses this callback's cleanup
  // to signal unmount. Replacing it during a render would close the host too.
  const onContactPanelChange = useCallback((open: boolean): void => {
    setContactPanelOpen(open);
    if (!open) requestAnimationFrame(() => {
      if (document.activeElement === document.body) document.querySelector<HTMLElement>('[data-testid="home-tasks-panel"]')?.focus();
    });
  }, []);
  const collectTrigger = useRef<HTMLElement | null>(null);
  const collectOrigin = useRef<{ appointmentId: number; fromQuick: boolean } | null>(null);
  const [collectionDoorError, setCollectionDoorError] = useState<string | null>(null);
  const collectionCheck = useRef(0);
  useEffect(() => { setCollectionDoorError(null); }, [activeClinicId, day.date, quickAppointmentId]);
  useEffect(() => () => { collectionCheck.current += 1; }, [activeClinicId, day.date, quickAppointmentId]);
  const openCollectPanel = useCallback((id: number) => {
    const row = appts.kind === 'ready' ? appts.data.find(item => item.id === id) : null;
    if (!row || day.scope === 'future' || activeClinicId === null) return;
    const check = ++collectionCheck.current;
    const clinicId = activeClinicId;
    collectTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCollectionDoorError(null);
    void fetchCheckoutHandoff(id).then(handoff => {
      if (check !== collectionCheck.current) return;
      if (handoff.appointmentId !== id || handoff.clinicId !== clinicId ||
          (row.patientId && handoff.patientId !== row.patientId)) throw new Error('Payment details changed. Reopen the visit.');
      const due = collectionOutstandingMinorUnits(handoff);
      if (typeof due !== 'number' || !Number.isSafeInteger(due)) { setCollectionDoorError('Amount due is unavailable. Refresh the visit before collecting payment.'); return; }
      if (due <= 0) { setCollectionDoorError('Nothing is due for this visit.'); reloadAppointments(); return; }
      collectOrigin.current = { appointmentId: id,
        fromQuick: Boolean(collectTrigger.current?.closest('[data-testid="home-quick-selected-appointment"]')) };
      setCollectAppointmentId(id);
    }).catch(() => { if (check === collectionCheck.current) setCollectionDoorError('Payment details could not be verified. Try again.'); });
  }, [activeClinicId, appts, day.date, day.scope, reloadAppointments]);
  useEffect(() => {
    if (collectAppointmentId !== null || !collectOrigin.current || appts.kind !== 'ready') return;
    const origin = collectOrigin.current;
    const frame = requestAnimationFrame(() => {
      // The child replaces its parent panel. Restore the logical trigger inside
      // that parent, never the identically named Collect button on the board.
      const root = origin.fromQuick && quickOpen && quickAppointmentId === origin.appointmentId
        ? document.querySelector<HTMLElement>('[data-testid="home-quick-selected-appointment"]') : document;
      const previous = collectTrigger.current;
      const testId = previous?.dataset.testid;
      const target = root?.querySelector<HTMLElement>(`[data-testid="${CSS.escape(testId ?? `day-board-collect-${origin.appointmentId}`)}"]`)
        ?? (previous?.isConnected ? previous : null)
        // Full payment can remove Collect. Keep a quick-view return inside
        // its visible appointment summary; a row return uses the patient door.
        ?? (root instanceof HTMLElement ? root : null)
        ?? document.querySelector<HTMLElement>(`[data-testid="day-board-open-${origin.appointmentId}"]`)
        ?? document.querySelector<HTMLElement>('[data-testid="home-finished"] > summary');
      if (target && !target.hasAttribute('disabled')) {
        // A saved collection refresh can remount Finished collapsed. Reveal
        // the lawful return target before focusing its current DOM instance.
        for (let ancestor = target.parentElement; ancestor; ancestor = ancestor.parentElement) {
          if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
        }
        target.focus();
        if (document.activeElement === target) {
          collectTrigger.current = null; collectOrigin.current = null;
        }
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [collectAppointmentId, appts.kind, quickOpen, quickAppointmentId]);
  const closeCollectDrawer = useCallback(() => { setCollectAppointmentId(null); }, []);
  useEffect(() => {
    if (!hasMe) {
      setAppts({ scopeKey: null, state: { kind: 'loading' } });
      return;
    }
    if (activeClinicId === null) {
      setAppts({ scopeKey: appointmentsScope, state: {
        kind: 'ready',
        data: [],
        clinicDoctors: [],
        contactPointLookupDegraded: false,
      } });
      return;
    }
    let cancelled = false;
    // THE ONE scoped-query descriptor (A5): exactly one clinic, exactly one
    // day. Changing the day re-issues THIS read and nothing else -- the
    // workstation feed, the follow-ups panel and /me are untouched, so a day
    // switch can never become a page-sized refetch.
    const dayRequest = dayScopeRequest({ date: day.date, clinicId: activeClinicId });
    const request = { ...dayRequest, key: appointmentsScope };
    // A7 STALE-RESPONSE LAW: rapid previous / next issues several reads; only
    // the response still holding the latest request identity may paint, so a
    // slow earlier day can never render its rows under a newer date. The
    // cleanup flag covers unmount; the gate covers OVERLAP.
    day.gate.issue(request.key);
    setAppts(previous => previous.scopeKey === request.key && previous.state.kind === 'ready'
      ? previous : { scopeKey: request.key, state: { kind: 'loading' } });
    fetchTodayAppointments(
      request.clinicId ?? undefined,
      boardMode ? 'board' : undefined,
      request.date,
      rangeEnd,
    )
      .then((data) => {
        if (!cancelled && day.gate.accepts(request.key)) {
          setNowMs(Date.now());
          setAppts({ scopeKey: request.key, state: {
            kind: 'ready',
            data: data.appointments,
            clinicDoctors: data.clinicDoctors ?? [],
            contactPointLookupDegraded: data.contactPointLookupDegraded ?? false,
          } });
        }
      })
      .catch(() => {
        if (!cancelled && day.gate.accepts(request.key)) setAppts({ scopeKey: request.key, state: { kind: 'error' } });
      });
    return () => {
      cancelled = true;
    };
  }, [hasMe, activeClinicId, boardMode, apptsReloadKey, appointmentsScope, day.date, day.gate, rangeEnd]);

  if (authLoading || meCtx?.loading) {
    return (
      <p className="walkin-status" role="status" aria-live="polite">
        Loading your workspace...
      </p>
    );
  }

  // Truly signed out (no Supabase session) -> ask to sign in.
  if (!user) {
    return (
      <div className="walkin-signin" role="status">
        <p>Sign in to register walk-ins.</p>
        <Link to="/login" className="btn btn-primary walkin-signin-link">
          Sign in
        </Link>
      </div>
    );
  }

  // Signed in but /me did not resolve (e.g. a transient access load failure):
  // do NOT push a signed-in user to re-authenticate as if logged out - offer a
  // retry-flavoured recovery instead.
  if (!hasMe) {
    return (
      <div className="walkin-signin" role="alert">
        <p>We couldn&apos;t load your workspace. Refresh the page to retry.</p>
        <Link to="/login" className="btn btn-primary walkin-signin-link">
          Sign in again
        </Link>
      </div>
    );
  }

  const appointments = appts.kind === 'ready' ? appts.data : [];
  const appointmentsAvailable = appts.kind === 'ready';
  // The URL carries the exact visit, so Back and reload return to the same
  // row. A missing row is unavailable; never substitute today's focus.
  const coverSelectedRow = coverSelection?.clinicId===activeClinicId && coverSelection.date===day.date && appointmentsAvailable
    ? appointments.find(row=>row.id===coverSelection.appointmentId) ?? null : null;
  const quickRow = quickOpen && appointmentsAvailable
    ? appointments.find(row => row.id === quickAppointmentId) ?? null
    : null;
  // CHECK OUT PATIENTS view: the rows the board TABLE renders under the
  // selected day view (a pure cut over the same fetched rows). Every head
  // count and tile keeps deriving from the WHOLE day's `appointments`.
  const viewRows = [...dayBoardViewRows(appointments, dayView)].sort((a, b) => {
    if (me?.role === 'receptionist') {
      const dayOrder = clinicToday(new Date(a.appointmentTime)).localeCompare(clinicToday(new Date(b.appointmentTime)));
      const booked = (value: string | null | undefined) => value && Number.isFinite(Date.parse(value)) ? Date.parse(value) : -Infinity;
      const aBooked = booked(a.bookedAt), bBooked = booked(b.bookedAt);
      if (dayOrder) return dayOrder;
      if (aBooked !== bBooked) return aBooked > bBooked ? -1 : 1;
    }
    return a.appointmentTime.localeCompare(b.appointmentTime) || a.id - b.id;
  });
  // An unverified balance is not evidence of settlement. Keep a completed
  // visit visible until the existing money read explicitly reports zero.
  const finishedRows = viewRows.filter(row =>
    row.status === 'cancelled' || row.status === 'no_show' ||
    ((row.status === 'fulfilled' || row.status === 'completed' || row.visitStatus === 'finalized') &&
      row.outstandingMinorUnits === 0));
  const finishedIds = new Set(finishedRows.map(row => row.id));
  const activeRows = viewRows.filter(row => !finishedIds.has(row.id));
  const returning = returningVisitCount(appointmentsAvailable ? appointments : null);
  const homeSummary = receptionDaySummary(appointmentsAvailable ? appointments : null);
  // Count only verified classifications. Unknown histories contribute no
  // New/Returning badge or count; an unavailable source remains unavailable.
  const returningDisplayValue = appts.kind === 'loading' ? '-' : returning.value;
  const checkedOutViewEmpty =
    dayView === 'checked_out' &&
    appointmentsAvailable &&
    appointments.length > 0 &&
    viewRows.length === 0;

  // F4b (RECEPTIONIST-EXPERIENCE-STITCH-V1): the old "View all" targeted the
  // receptionist Workstation, which is now FOLDED into this Today home (F2), so
  // the link would loop straight back here. Omit it -- the recent walk-ins
  // already render below; a dedicated recent-activity view is part of the Today
  // command centre (V3-P1, deferred). No dead-end link.
  const viewAllHref = null;

  // STITCH-2 SCOPE 12: the snapshot strip -- EXISTING sources only (the
  // workstation feed + the today-appointments read already fetched above);
  // seams never fake data (an unavailable feed renders an honest dash).
  //
  // FRONT-OFFICE-DENSITY-PASS-V1 (D4): ONE next-expected DERIVATION. This tile
  // and the day board's own "Next" row badge used to run two independent finds
  // over the same rows -- they now consume THE ONE exported derivation
  // (nextExpectedRowId), so the tile can never name a different patient than the
  // row the board marks.
  // "Next expected" is a NOW fact: on a past day a still-booked row means
  // nobody came, not somebody due. Withheld off today (A6).
  const nextExpectedId =
    appointmentsAvailable && day.scope === 'today' && !rangeEnd
      ? nextExpectedRowId(appointments)
      : null;
  const nextExpected =
    nextExpectedId === null
      ? undefined
      : appointments.find((a) => a.id === nextExpectedId);

  // FD-42: the action strip's counts, off the SAME rows (one derivation).
  //
  // A6 TRUTH LAW: these four are TODAY-SHAPED facts -- "waiting" and "late"
  // are measured against the live clock, and "needs registration" is an owed
  // desk act. Off today they are structurally withheld (never relabelled,
  // never printed against a stale day): a past day would report every
  // still-booked row as "late" and a future day would advertise arrivals that
  // have not happened. On today every tile is byte-identical.
  const actionCounts =
    appointmentsAvailable && day.scope === 'today' && !rangeEnd
      ? deskActionCounts(appointments, nowMs)
      : null;

  // HANDOFF LAW (CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1): the To-collect
  // glance -- doctor-accepted, unpaid visits from the SAME board rows (one
  // computation; boardMode only -- the plain view lacks the derivation
  // fields, so it stays an honest dash).
  const toCollect =
    boardMode && appointmentsAvailable && day.scope !== 'future' && !rangeEnd
      ? collectQueueSummary(appointments)
      : null;

  // The ACTIVE clinic, always named. 119-CLINIC-OPERATING-SPINE-V1 drops the
  // `clinicIds.length > 1` condition: a single-clinic operator was shown NO
  // clinic at all, so the one surface that must always answer "which clinic am
  // I working in" answered it only sometimes. It now always states its clinic,
  // through THE ONE label primitive.
  const activeClinicOption =
    clinicCtx?.options.find((o) => o.clinicId === activeClinicId) ?? null;
  const activeClinicLabel =
    activeClinicOption !== null ? clinicOptionLabel(activeClinicOption) : null;

  const renderBoard = (boardRows: TodayAppointment[], boardDate: string): JSX.Element => (
          <ClinicDayBoard
            bookingClinic={activeClinicId !== null ? {id:activeClinicId,label:activeClinicLabel} : null}
            requestedAction={quickRequestedAction}
            onRequestedActionHandled={() => setQuickRequestedAction(null)}
            compactHome={me?.role === 'receptionist'}
            rows={boardRows}
            {...(me?.role === 'receptionist' ? {
              onOpenLead: openVisit,
              onOpenPatientRecord: openPatientQuickView,
              contextDisabled: workSurfaceOpen || recordDoorBusy || collectAppointmentId !== null || contactPanelOpen || utilityPanel !== null || visitLead !== null || recordOpen,
            } : {})}
            role={me?.role ?? null}
            loading={appts.kind === 'loading'}
            error={appts.kind === 'error'}
            onChanged={reloadAppointments}
            chrome="embedded"
            /* DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1: WHICH day these rows are.
               The board's ONE lens then decides what a non-today day may
               render (no action strip, no payment / case-sheet / assign /
               intake door, no live clocks, scope-shaped empty state) -- this
               host states the day and nothing else. `nowMs` is still passed
               unconditionally; the board withholds the clock itself off
               today, so the rule lives in exactly one place. */
            dayScope={resolveDayScope(boardDate, day.today)}
            nowMs={nowMs}
            statusLabels={me?.appointmentStatusLabels ?? null}
            scrollHighlightIntoView={!quickOpen}
            highlightAppointmentId={collectAppointmentId ?? (quickOpen ? quickAppointmentId : (coverMode || noticeAppointment!==null) && coverSelectedRow ? coverSelectedRow.id : highlightAppointmentId)}
            clinicDoctors={appts.kind === 'ready' ? appts.clinicDoctors : []}
            {...(me?.role === 'doctor'
              ? {
                  // SCOPE 4: the doctor's case-sheet door from the day board -
                  // the EXISTING clinical-workbench cockpit, prefilled by the
                  // row's appointment id (server gate doctor-only, unchanged).
                  caseSheetAction: (row: TodayAppointment) =>
                    row.status === 'checked_in' ||
                    row.status === 'fulfilled' ||
                    row.status === 'completed' ? (
                      <Link
                        to={`/dashboard/clinical-workbench?appointmentId=${row.id}`}
                        className="ac-btn ac-btn--primary ac-btn--sm"
                        data-testid={`day-board-casesheet-${row.id}`}
                      >
                        {row.visitStatus === 'none'
                          ? 'Start visit'
                          : 'Open visit'}
                      </Link>
                    ) : null,
                }
              : {})}
            {...(me?.role === 'receptionist'
              ? {
                  // SCOPE 5 (TRAIN-B) upgraded by CHECKOUT-HANDOFF-AND-
                  // DOCTOR-CONTEXT-V1 (HANDOFF LAW): the ONE payment door
                  // (Collect CTA / no-handoff line / quiet Record payment).
                  // The payment writer's route gate stays the boundary.
                  paymentAction: (row: TodayAppointment) => (
                    <ReceptionistPaymentDoor
                      row={row}
                      activeClinicId={activeClinicId}
                      onCollect={openCollectPanel}
                      collectDisabled={collectAppointmentId !== null || contactPanelOpen || utilityPanel !== null || visitLead !== null || recordOpen}
                    />
                  ),
                  // FRONTDESK-CHECKIN-AND-IDENTITY-V1: the check-in identity
                  // step's UX gate (Scope 1f) + the org's cancel-reason
                  // vocabulary (rider R-08). The backend fences stay the
                  // boundary for both.
                  checkinIdentity: { bridgeEnabled: canUseLeadBridge(me) },
                  cancelReasonCodes: resolveCancelReasonCodes(me),
                  // PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1:
                  // the intake-spine lens (absent /me key = disabled ->
                  // the V3-P6 workflow byte-identical; fail-closed).
                  intakeSpine: { enabled: me?.intakeSpineEnabled === true },
                }
              : {})}
            // SEP15 vertical: the Patient 360 continuity door on linked rows
            // (role-shaped in the board; reachability from the /me capability
            // signal -- fail-closed, LES-016: never a dead door).
            patientRecordDoor={{ enabled: me?.patientMasterEnabled === true }}
          />
  );

  return (
    <main className="ac-page today-home" aria-label="Home" data-compact-reception={me?.role === 'receptionist' ? 'true' : undefined}
      data-money-detail={quickOpen && !workSurfaceOpen && !recordOpen && collectAppointmentId === null &&
        (quickDetail === 'bill' || quickDetail === 'payments') ? 'true' : undefined}>
      {/* UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2): the shared
          role-home header. FO-2: the landing page is HOME -- today's day
          board lives INSIDE it as the primary content below. */}
      <PageHeader
        title="Home"
        actions={
          activeClinicLabel !== null ? (
            <ClinicScopeLabel
              label={activeClinicLabel}
              testId="home-clinic-context"
            />
          ) : null
        }
      />
      <WorkspaceSurfaceContext.Provider value={me?.role === 'receptionist'
        ? { target: workSurfaceTarget, onOpenChange: onWorkSurfaceChange } : null}>
      <div className="ac-workspace-panels">
      <MasterDetail single={!searchPanelOpen && noticeAppointment===null && !workSurfaceOpen && collectAppointmentId === null && !contactPanelOpen && utilityPanel === null && visitLead === null && !recordOpen && !quickOpen} testId="frontdesk-workspace">
      <div className="ac-workspace-panels__list">
      {/* FO-3: GLOBAL SEARCH is the compact entry -- one box, mobile /
          UMR / name. It replaces the standalone registration block: the
          centered modal carries results, the shared-phone family list, and
          the register door (search-first law C13 -- no creation door before
          search evidence). */}
      <RoleHomeSearchPortal>
        <ReceptionSearch key={`${user?.id}:${me?.organizationId}:${me?.role}:${activeClinicId}:${me?.patientMasterEnabled}:${me?.clinicIds.join(',')}`}
          scopeKey={`${user?.id}:${me?.organizationId}:${me?.role}:${activeClinicId}:${me?.patientMasterEnabled}:${me?.clinicIds.join(',')}`}
          clinicId={activeClinicId} clinicLabel={activeClinicLabel??'Clinic'} disabled={authLoading||!user||me?.role!=='receptionist'||me?.patientMasterEnabled!==true||activeClinicId===null||workSurfaceOpen}
          panelTarget={searchPanelTarget} onPanelChange={setSearchPanelOpen}
          onChanged={()=>{reloadAppointments();loadWorkstation();}}
          onAppointment={(id,iso)=>{const next=new URLSearchParams(walkinBoardHref(iso,id).split('?')[1]??'');next.set('quickAppointment',String(id));next.delete('searchPatient');next.delete('searchQuery');setSearchParams(next);reloadAppointments();}}
        />
      </RoleHomeSearchPortal>
      {/* DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1: THE DAY REGION begins here.
          Everything below re-renders IN PLACE when the day changes -- the
          bar, the glance strip and the board. There is no navigation and no
          second day surface: Home IS the operating surface for the selected
          clinic day (founder ruling 2026-08-04).

          THE ONE registered day control, identical to the Appointments
          planning screen's (same component, same markup, same four moves).
          It renders only in boardMode, because that is the only lens with a
          day surface underneath it -- a control with nothing to steer is a
          dead door (LES-016). */}
          {me?.role==='receptionist'&&activeClinicId!==null&&!rangeEnd?<ReceptionCoverSummary key={`${activeClinicId}:${day.date}`} clinicId={activeClinicId} clinicName={activeClinicLabel??'Clinic'} date={day.date} revision={apptsReloadKey} doctors={appts.kind==='ready'?appts.clinicDoctors:[]} disabled={taskBusy||visitBusy||workSurfaceOpen||recordOpen||contactPanelOpen||collectAppointmentId!==null||coverMode!==null} onChanged={reloadAppointments} onReport={event=>{setNoticeAppointment(null);coverTrigger.current=event.currentTarget;setUtilityPanel(null);setVisitLead(null);closePatientQuickView(false);setCoverMode('report')}}/>:null}
      <div className="today-home__day-tools">
      {me?.role === 'receptionist' ? <DaySummaryRibbon label={rangeEnd ? "Selected clinic dates" : "Selected clinic day"} testId="reception-day-summary"
        seam={appts.kind === 'loading' ? undefined : homeSummary === null ? 'Schedule unavailable right now' : undefined}
        cells={[
          {key:'waiting', label:'Waiting', value:homeSummary?.waiting ?? '-', testId:'today-action-waiting'},
          {key:'consult', label:'In consult', value:homeSummary?.inConsult ?? '-', testId:'today-action-in-consult'},
          {key:'completed', label:'Completed', value:homeSummary?.completed ?? '-', testId:'today-action-completed'},
          {key:'expected', label:'Expected', value:homeSummary?.expected ?? '-', testId:'today-action-expected'},
          {key:'cancelled', label:'Cancelled', value:homeSummary?.cancelled ?? '-', testId:'today-action-cancelled'},
          {key:'new', label:'New', value:homeSummary === null ? (appts.kind === 'loading' ? '-' : 'Unavailable') : homeSummary.newVisits, testId:'today-new-count'},
          {key:'returning', label:'Returning', value:<span title={returning.explanation} aria-label={returning.explanation}>{returningDisplayValue}</span>, testId:day.scope === 'today' && !rangeEnd ? 'today-returning-count' : 'selected-returning-count'},
        ]} /> : null}
      {boardMode ? (
        /* CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D1): COMPACT IS THE
           STANDARD for role-home and day-board surfaces -- the doctor's
           grammar, extended beyond the doctor. The joined pill, its border,
           its hairline dividers and the brand-coloured Today anchor now live
           with the variant in the shared layer (measured fact C3), so the
           switch carries the whole grammar rather than chevrons alone. Same
           component, same four moves, same day state, one visible date. */
        <fieldset className="today-home__date-controls" disabled={workSurfaceOpen}>
        <DateCommandBar
          date={day.date}
          today={day.today}
          onSelect={selectAppointmentDate}
          onStep={(delta) => selectAppointmentDate(stepClinicDate(day.date, delta))}
          onToday={() => selectAppointmentDate(day.today)}
          variant="compact"
          showToday={me?.role !== 'receptionist'}
          showScope={me?.role !== 'receptionist'}
          openPickerOnClick={me?.role === 'receptionist'}
        />
        </fieldset>
      ) : null}

      {/* FD-42 (RECEPTION-FIX-AND-POLISH-BATCH-V1): the ACTION summary
          strip -- the role's four questions (who's waiting / late / needs
          payment / needs registration), derived from the SAME board rows
          (deskActionCounts + THE ONE collect derivation). Facts the desk
          ACTS on, not raw volumes; the day count + next-expected facts
          live ONCE in the day-board head below (SAY IT ONCE). Renders the
          shell's tile strip (UX LAWS 2/3/4).

          A6: the strip is TODAY ONLY. Its four questions are all "right
          now" questions, and a strip of honest dashes on every past day is
          noise, not truth -- so off today it is absent and the board below
          carries the day's recorded or planned tally instead. */}
      {me?.role !== 'receptionist' && (boardMode || (day.scope === 'today' && !rangeEnd)) ? (
        /* CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D4): the desk's five
           action counts move onto THE SHARED day-summary ribbon -- the
           doctor's quiet grammar, carrying the FRONT DESK's own metrics.
           The doctor does not move to large tiles; the tiles come to the
           ribbon. Same facts, same one-pass derivation, same numbers, same
           labels, same testids: only the carrier changed. The five tiles'
           sub-sentences retire WITH the tile that carried them -- a ribbon
           cell is a number and its word -- and the honest-absence signal
           they carried survives as the same '-' the doctor's ribbon prints
           when the day read has not landed (never a fake zero).

           D7 rides for free and cannot drift per role, because the shared
           primitive decides it: "Cancelled 0" recedes while "Late 1" stays
           loud, so the five facts are weighted by attention instead of
           being five equal boxes shouting equally.

           DISPLAY ONLY, unchanged: no count is clickable and none filters
           the board -- whether a count filters in place or navigates is
           undefined behaviour and stays out (founder, 2026-08-31).

           A6 (carried): the strip is TODAY ONLY. Its questions are all
           "right now" questions, so off today it is absent and the board
           below carries the day's recorded or planned tally instead. */
        <section className="today-home__more-counts" aria-label="Day totals">
        <div style={{ '--ac-space-4': 'var(--ac-space-2)' } as CSSProperties}>
        {boardMode ? <>
          <AcButton size="sm" variant="ghost" disabled={workSurfaceOpen} aria-expanded={rangePickerOpen}
            onClick={() => { setRangeStartDraft(day.date); setRangeEndDraft(rangeEnd ?? day.date); setRangePickerOpen((open) => !open); }}
            data-testid="home-range-open">{rangeEnd ? `Through ${clinicDateLabel(rangeEnd)}` : 'Date range'}</AcButton>
          {rangePickerOpen ? <form className="today-home__range" onSubmit={(event) => {
            event.preventDefault();
            if (workSurfaceOpen) return;
            if (!isClinicDate(rangeStartDraft) || !isClinicDate(rangeEndDraft) || rangeEndDraft < rangeStartDraft) {
              setRangeError('Choose an end date on or after the start date.'); return;
            }
            const params = new URLSearchParams(writeDayParam([...searchParams.entries()], rangeStartDraft, day.today));
            if (rangeEndDraft !== rangeStartDraft) params.set('endDate', rangeEndDraft); else params.delete('endDate');
            setSearchParams(params); setRangeError(null); setRangePickerOpen(false);
            // The compact Home exposes Date range directly; restore focus
            // to that surviving trigger when the range form unmounts.
            event.currentTarget.parentElement?.querySelector<HTMLElement>('[data-testid="home-range-open"]')?.focus();
          }}>
            <DateField id="home-range-from" label="Appointments from" disabled={workSurfaceOpen} value={rangeStartDraft} onChange={setRangeStartDraft} />
            <DateField id="home-range-to" label="Appointments to" disabled={workSurfaceOpen} value={rangeEndDraft} onChange={setRangeEndDraft} />
            <AcButton type="submit" size="sm" disabled={workSurfaceOpen}>Show appointments</AcButton>
            {rangeError ? <p role="alert">{rangeError}</p> : null}
          </form> : null}
        </> : null}
        {day.scope === 'today' && !rangeEnd ? <DaySummaryRibbon
          label="Today at a glance"
          testId="today-home-snapshot"
          /* SAY IT ONCE: the day read's dark state was repeated inside all
             five tile sub-lines; it is ONE line under the ribbon now, in the
             SAME words. The dashes above stay the honest value -- never a
             fabricated zero. */
          seam={
            appts.kind === 'loading' ? undefined : actionCounts === null ? 'Schedule unavailable right now' : undefined
          }
          cells={[
            {
              key: 'waiting',
              label: 'Waiting',
              value: actionCounts !== null ? actionCounts.waiting : '-',
              quiet: actionCounts === null ? true : undefined,
              testId: 'today-action-waiting',
            },
            {
              key: 'late',
              label: 'Late',
              value: actionCounts !== null ? actionCounts.late : '-',
              quiet: actionCounts === null ? true : undefined,
              testId: 'today-action-late',
            },
            /* The money fact keeps its EXACT existing shape: the total when
               the netting derivation resolved one, the visit count when it
               did not, and the same honest dash when the day read failed.
               A settled day is quiet by the same rule as a zero count. */
            ...(boardMode
              ? [
                  {
                    key: 'collect',
                    label: 'Needs payment',
                    value:
                      toCollect === null
                        ? '-'
                        : toCollect.totalAmount !== null
                          ? `INR ${toCollect.totalAmount.toLocaleString('en-IN')}`
                          : toCollect.count,
                    quiet: toCollect === null || toCollect.count === 0,
                    testId: 'today-action-collect',
                  },
                ]
              : []),
            {
              key: 'registration',
              label: 'Needs registration',
              value:
                actionCounts !== null ? actionCounts.needsRegistration : '-',
              quiet: actionCounts === null ? true : undefined,
              testId: 'today-action-registration',
            },
            /* EXISTING-SCREEN-COMPLETIONS-V1 (tenant tracker, FRONT DESK
               home "Cancelled"): the day's recorded cancelled tally, from
               the SAME deskActionCounts derivation as the facts beside it
               (one pass, counts == list). The stored-status predicate is the
               same one the day board's own state strip and the COC day-flow
               Cancelled tile count, so this number can never disagree with
               the board below. */
            {
              key: 'cancelled',
              label: 'Cancelled',
              value: actionCounts !== null ? actionCounts.cancelled : '-',
              quiet: actionCounts === null ? true : undefined,
              testId: 'today-action-cancelled',
            },
            {
              key: 'returning',
              label: 'Returning',
              value: <span title={returning.explanation} aria-label={returning.explanation}>{returningDisplayValue}</span>,
              testId: 'today-returning-count',
            },
          ]}
        /> : null}
        </div>
        </section>
      ) : me?.role !== 'receptionist' ? (
        <DaySummaryRibbon
          label="Selected date and view"
          cells={[{ key: 'returning', label: 'Returning', value: <span title={returning.explanation} aria-label={returning.explanation}>{returningDisplayValue}</span>, testId: 'selected-returning-count' }]}
        />
      ) : null}
      </div>

      {/* PRIMARY (TRAIN-B): the clinic DAY BOARD -- the H2 day home's primary
          panel for board-audience roles (receptionist runs the day; coc /
          owner / managers read it view-only). Walk-in capture stays a section
          below (the ratified WALK-IN FOLD). Non-board roles keep the original
          layout (the plan card in the activity rail). */}
      {boardMode ? (
        <section className="today-home__dayboard" aria-label="Clinic day board">
          {(collectionDoorError || recordDoorError) && !quickOpen ? <p role="alert">{collectionDoorError || recordDoorError}</p> : null}
          {/* FRONT-OFFICE-DENSITY-PASS-V1 (D4 + P14): a dense queue gets NO hero
              band. The board is hosted with chrome='embedded', so its own
              eyebrow + "Today's appointments" title + prose summary no longer
              render a SECOND hero on a page that already has the Today page
              header and the glance strip above. Nothing is lost: the day count
              lives ONCE in the "Appointments today" tile above, and the two
              remaining head facts render ONCE here, from THE SAME exported
              derivations the board itself uses (opSoFarCount / dayStateCounts --
              counts == list by construction, never a second computation). */}
          <div className="today-home__dayboard-head">
            <div className="today-home__board-context">
            {rangeEnd && appts.kind === 'error' ? <p role="alert">Could not load the complete range. Try a shorter date range.</p> : null}
            {/* CONTEXT-AWARE HEADING (B2): today reads as today; any other
                day names its date. ONE copy owner -- the day-scope
                vocabulary -- so no surface mints a second set of day words. */}
            {me?.role !== 'receptionist' || rangeEnd ? <h2
              className="today-home__dayboard-title"
              data-testid="day-board-heading"
            >
              {rangeEnd ? `${clinicDateLabel(day.date)} – ${clinicDateLabel(rangeEnd)}` : dayScopeHeading(day.date, day.today)}
            </h2> : null}
            {/* The honest read-only line for a non-today day. Absent on
                today (the copy law forbids narrating the present); it
                explains the ONE thing the operator would otherwise have to
                guess -- why the row actions are gone. */}
            {me?.role === 'receptionist' && !rangeEnd && day.scope === 'past' ? <p className="day-board__viewonly" data-testid="reception-past-day-notice">
              This day is read-only. Choose today for actions.{' '}<AcButton variant="secondary" size="sm" onClick={() => selectAppointmentDate(day.today)} disabled={workSurfaceOpen}>Today</AcButton>
            </p> : null}
            {me?.role !== 'receptionist' && !rangeEnd && dayScopeReadOnlyCopy(day.scope) !== null ? (
              <p
                className="day-board__viewonly"
                data-testid="day-board-dayscope"
              >
                {dayScopeReadOnlyCopy(day.scope)}
              </p>
            ) : null}
            {me?.role !== 'receptionist' ? <><AcButton variant="ghost" size="sm" aria-expanded={totalsOpen}
              onClick={() => setTotalsOpen((open) => !open)} data-testid="home-day-totals">Day totals</AcButton>
            <div hidden={!totalsOpen} className="today-home__totals">
            {appointmentsAvailable && appointments.length > 0 ? (
              <p className="day-board__counts" data-testid="day-board-counts">
                {/* FD-42 (SAY IT ONCE): the day total + next-expected facts
                    moved HERE from the retired volume tiles -- one home. */}
                <span className="day-board__count" data-testid="day-board-total">
                  Appointments <strong>{appointments.length}</strong>
                </span>
                {/* "OP so far" describes a day IN PROGRESS -- withheld off
                    today; the per-state counts below stay honest for any
                    day (they are plain tallies of the rows on screen). */}
                {day.scope === 'today' && !rangeEnd ? (
                  <span className="day-board__count" data-testid="day-board-op-so-far">
                    OP so far <strong>{opSoFarCount(appointments)}</strong>
                  </span>
                ) : null}
                {dayStateCounts(appointments, me?.appointmentStatusLabels ?? null).map((c) => (
                  <span
                    key={c.status}
                    className="day-board__count"
                    data-testid={`day-board-count-${c.status}`}
                  >
                    {c.label} <strong>{c.count}</strong>
                  </span>
                ))}
                {nextExpected ? (
                  <span className="day-board__count" data-testid="day-board-next-expected">
                    {/* T7: the shared missing-time mark ('--:--', the board
                        + doctor grammar) -- the lone '-' fallback was the
                        one dissenting form on this surface. */}
                    Next expected{' '}
                    <strong>
                      {formatIstTime(nextExpected.appointmentTime) || '--:--'}
                    </strong>{' '}
                    {dayBoardPatientLabel(nextExpected)}
                  </span>
                ) : null}
              </p>
            ) : null}
            </div></> : null}
            {/* SEE-NOT-DO honesty for the view-only lenses, from the board's ONE
                copy owner -- the embedded chrome drops the board's own head, so
                the host must not drop the line with it. */}
            {!canActOnDayBoard(me?.role ?? null) ? (
              <p className="day-board__viewonly" data-testid="day-board-viewonly">
                {DAY_BOARD_VIEW_ONLY_COPY}
              </p>
            ) : null}
            </div>
            {/* FRONT-DESK-DAY-BOARD-V1 ("CHECK OUT PATIENTS"): the two named
                day views over the SAME fetched rows, through THE registered
                view-chip row (ViewChips; C-11 role-default presets, C-9
                URL-canonical state). The head counts above keep describing
                the WHOLE day, so narrowing the table never rewrites the
                day's tally. */}
            {me?.role !== 'receptionist' ? <div className="ac-selbar">
            <ViewChips
              views={DAY_BOARD_VIEWS.map((v) => ({
                id: v.id,
                label: v.label,
                hint: v.hint,
              }))}
              activeViewId={dayView}
              onSelect={selectDayView}
              onCopyUrl={onCopyViewUrl}
              copied={viewUrlCopied}
              showCopyLink={false}
            />
            <details style={{ marginLeft: 'auto' }} data-testid="home-more">
              <summary className="ac-viewchips__copy">More</summary>
              <AcButton type="button" variant="ghost" size="sm" onClick={onCopyViewUrl} data-testid="home-copy-view">
                {viewUrlCopied ? 'Link copied' : 'Copy link to this view'}
              </AcButton>
            </details>
            </div> : dayView === 'checked_out' ? <AcButton variant="ghost" size="sm" onClick={() => selectDayView('everyone')}>Back to all appointments</AcButton> : null}
          </div>
          {/* A7 FUNCTIONAL MOTION + STATE HYGIENE: the day RESULT region is
              keyed on the selected day, so a day switch (a) replays the
              one settle transition the design language allows for a
              date-result change -- disabled entirely under
              prefers-reduced-motion -- and (b) remounts the board with
              FRESH local state. (b) is the correctness half: a success
              message or a busy row belongs to the day it happened on, and
              must never survive onto another day's rows. */}
          <div
            className="ac-dayregion__result"
            key={`${day.date}:${rangeEnd ?? ''}`}
            data-testid="day-region-result"
            tabIndex={-1}
          >
          {checkedOutViewEmpty ? (
            /* The DAY has rows while the checked-out VIEW has none: the
               board's own no-appointments empty copy would be false here,
               so the host renders the view's honest line instead
               (DAY_VIEW_CHECKED_OUT_EMPTY_COPY -- one copy owner). */
            <p
              className="day-board__viewonly"
              data-testid="day-board-view-empty"
            >
              {DAY_VIEW_CHECKED_OUT_EMPTY_COPY}
            </p>
          ) : (
          rangeEnd && appointmentsAvailable && viewRows.length > 0 ? (
            Array.from(new Set(viewRows.map((row) => clinicToday(new Date(row.appointmentTime))))).sort().map((date) => (
              <section key={date} aria-label={`Appointments ${clinicDateLabel(date)}`}>
                <h3 className="today-home__range-heading">{clinicDateLabel(date)}</h3>
                {renderBoard(viewRows.filter((row) => clinicToday(new Date(row.appointmentTime)) === date), date)}
              </section>
            ))
          ) : me?.role === 'receptionist' && dayView === 'everyone' && appointmentsAvailable ? <>
            {renderBoard(activeRows, day.date)}
            <details className="today-home__finished" data-testid="home-finished"
              open={finishedOpen} onToggle={event => setFinishedOpen(event.currentTarget.open)}>
              <summary>Finished · {finishedRows.length}</summary>
              {finishedRows.length > 0 ? renderBoard(finishedRows, day.date) : <p>No finished visits.</p>}
            </details>
          </> : renderBoard(viewRows, day.date)
          )}
          </div>
          {me?.role === 'receptionist' ? <section className="today-home__booking-notices" data-testid="home-cover-notices">
            <details><summary>Booking notices</summary><div className="ac-action-row">
            <AcButton title={(quickRow??coverSelectedRow) ? undefined : 'Select an appointment first'} disabled={!(quickRow??coverSelectedRow)||contactPanelOpen||taskBusy||visitBusy||workSurfaceOpen||recordOpen||collectAppointmentId!==null} onClick={event=>{const selected=quickRow??coverSelectedRow;if(!selected||activeClinicId===null)return;setCoverSelection({clinicId:activeClinicId,date:day.date,appointmentId:selected.id});coverTrigger.current=event.currentTarget;setCoverMode(null);setUtilityPanel(null);setVisitLead(null);setNoticeAppointment(selected.id);closePatientQuickView(false);}}>Booking notice · not sent</AcButton></div></details>
          </section> : null}
        </section>
      ) : null}

      {/* Recent walk-ins and the existing follow-up workflows are now hosted
          by the utility panels below; their reads and actions are retained. */}
      </div>
      {/* SMART-PAYMENT-DRAWER-V1: collect THIS visit's payment IN PLACE. No
          url change on the normal path; the board refreshes once the payment
          commits so the row's state is honest the moment the drawer closes. */}
      <DetailAside label="Selected task">
      <div ref={setSearchPanelTarget} />
      {noticeAppointment!==null&&activeClinicId!==null?<ReceptionNoticePanel key={`${activeClinicId}:${noticeAppointment}`} clinicId={activeClinicId} appointmentId={noticeAppointment} onClose={()=>{setNoticeAppointment(null);requestAnimationFrame(()=>coverTrigger.current?.isConnected?coverTrigger.current.focus():document.querySelector<HTMLElement>('[data-testid="home-cover-notices"] button')?.focus());}}/>:null}


      <div ref={setContactPanelTarget} />
      <div ref={setWorkSurfaceTarget} data-testid="home-focused-work-host" />
      {/* Mounted independently of the appointment date: changing a day never
          refetches, filters or discards the operator's task queue. */}
      <div hidden={utilityPanel !== 'tasks' || contactPanelOpen || visitLead !== null || recordOpen}>
        <WorkPanel title={taskCount === null ? 'Follow-ups' : `Follow-ups · ${taskCount}`}
          onClose={closeUtility} closeDisabled={taskBusy} testId="home-tasks-panel">
          <WalkinFollowupsPanel compact onSummary={onTaskSummary}
            contactPanel={{ panelTarget: contactPanelTarget, onPanelChange: onContactPanelChange,
              taskDisabled: collectAppointmentId !== null || contactPanelOpen || visitLead !== null || taskBusy }} />
        </WorkPanel>
      </div>
      {utilityPanel === 'registrations' ? (
        <WorkPanel title="Recent walk-ins" onClose={closeUtility} testId="home-registrations-panel">
          <WalkinActivityPanel
            available={wsAvailable}
            recentWalkins={wsData?.recentWalkins ?? []}
            todayWalkinCount={wsAvailable ? (wsData?.todayWalkinCount ?? 0) : null}
            appointments={appointments}
            appointmentsAvailable={appointmentsAvailable}
            viewAllHref={viewAllHref}
            showPlan={!boardMode}
            statusLabels={me?.appointmentStatusLabels ?? null}
            // X12 (Lane 3, 2026-09-10): the audited source-correction door,
            // reception-lens only -- the backend independently enforces
            // APPOINTMENT_ACTION_ROLES regardless of what this UI renders
            // (LAW 2: route fence is not field fence).
            {...(me?.role === 'receptionist'
              ? {
                  sourceCorrectionAction: (w: WorkstationRecentWalkin) => (
                    <WalkinSourceCorrectionDoor walkin={w} onCorrected={loadWorkstation} />
                  ),
                }
              : {})}
          />
        </WorkPanel>
      ) : null}
      {visitLead !== null ? (
        <WorkPanel title="Patient details" onClose={closeVisit} closeDisabled={visitBusy} testId="home-visit-panel">
          <LeadQuickViewHost frame="panel" leadId={visitLead.leadId} contextOnly
            seedTitle={visitLead.title} onBusyChange={setVisitBusy} onClose={closeVisit} onChanged={reloadAppointments} />
        </WorkPanel>
      ) : null}
      {rescheduleRow && activeClinicId !== null ? <RescheduleAppointmentDialog
        key={`${activeClinicId}:${rescheduleRow.id}`} row={rescheduleRow} clinicId={activeClinicId}
        onClose={closeReschedule} onSaved={savedDate => {
          closeReschedule();
          const params = new URLSearchParams(searchParams);
          params.set('date', savedDate);
          params.delete('endDate');
          params.set('quickAppointment', String(rescheduleRow.id));
          setSearchParams(params);
          reloadAppointments();
        }} /> : null}
      {quickOpen && rescheduleRow === null && !workSurfaceOpen && !recordOpen && collectAppointmentId === null ? (
        <WorkPanel eyebrow="Details" title={correctionReceiptPreview ? 'Payment receipt' : quickDetail === 'bill' ? 'Itemised bill' : quickDetail === 'payments' && Number.isSafeInteger(Number(searchParams.get('quickDocument'))) && Number(searchParams.get('quickDocument')) > 0 ? 'Payment receipt' : 'Patient quick view'} onClose={() => closePatientQuickView()} testId="home-patient-quick-view">
          {appts.kind === 'loading' ? <p role="status">Loading appointment...</p> : quickRow === null ? (
            <p role="status" data-testid="home-quick-visit-unavailable">This appointment is not available in the selected clinic and day. Return to the day board.</p>
          ) : quickDetail !== null && activeClinicId !== null ? (
            <PatientQuickDetails key={`${activeClinicId}:${quickRow.id}`} row={quickRow} patientName={quickRow.linkedPatientName ?? quickRow.patientName ?? undefined} clinicId={activeClinicId}
              detail={quickDetail} onBack={closeQuickDetail} onDocumentPreviewChange={onDocumentPreviewChange} />
          ) : (
            <section aria-label="Selected appointment" tabIndex={-1} data-testid="home-quick-selected-appointment">
              <h3>{dayBoardPatientLabel(quickRow)}</h3>
              {quickRow.linkedPatientPriorVisit ? <p>{quickRow.linkedPatientPriorVisit === 'new' ? 'New patient' : 'Returning patient'}</p> : null}
              <dl className="today-home__quick-facts">
                {quickRow.linkedPatientUmr ? <div><dt>UMR</dt><dd>{quickRow.linkedPatientUmr}</dd></div> : null}
                <div><dt>Clinic</dt><dd>{activeClinicLabel ?? 'Unavailable'}</dd></div>
                <div><dt>Visit</dt><dd>#{quickRow.id}</dd></div>
                <div><dt>Status</dt><dd>{currentVisitStatusChip(quickRow, me?.appointmentStatusLabels).label}</dd></div>
                <div><dt>Registration</dt><dd>{quickRow.hasPatientRecord === true || quickRow.registrationCaptured === true ? 'Recorded' : quickRow.hasPatientRecord === false || quickRow.registrationCaptured === false ? 'Pending' : 'Unavailable'}</dd></div>
                <div><dt>Intake</dt><dd>{quickRow.intakeStatus === 'completed' ? 'Completed' : quickRow.intakeStatus === 'in_progress' ? 'Started' : quickRow.intakeStatus === 'none' ? 'Not started' : 'Unavailable'}</dd></div>
                {quickRow.bookingVisitType ? <div><dt>Appointment type</dt><dd>{bookingChannelLabel(quickRow.bookingVisitType)}</dd></div> : null}
                <div><dt>Channel</dt><dd>{quickRow.bookingChannel ? bookingChannelLabel(quickRow.bookingChannel) : 'Unavailable'}</dd></div>
              </dl>
              {(() => {
                const canAct = me?.role === 'receptionist' && !rangeEnd && day.scope === 'today';
                const futurePlanning = me?.role === 'receptionist' && !rangeEnd && day.scope === 'future' &&
                  ['booked', 'confirmed'].includes(quickRow.status);
                const intake = intakeRowPlan(quickRow, canAct && me?.intakeSpineEnabled === true);
                const collect = day.scope !== 'future' && !rangeEnd && checkoutHandoff(quickRow) !== null;
                const next = dayBoardNextAction(quickRow, { canAct, collectIsPrimary: collect,
                  intakeIsPrimary: intake.intakeIsPrimary, suppressCheckedIn: intake.suppressCheckedInAction });
                const primary = intake.intakeIsPrimary && !collect ? 'intake' : next.primary;
                const secondary = [next.secondary, ...next.overflow].filter((action): action is DayBoardActionKind => action !== null);
                if (futurePlanning) secondary.push('cancel');
                const canReschedule = activeClinicId !== null && (canAct || futurePlanning) &&
                  allowedWorkbenchActions(quickRow.status).includes('reschedule');
                const requestAction = (action: DayBoardActionKind | 'intake' | 'assign_doctor') => {
                  setQuickRequestedAction({ appointmentId: quickRow.id, action, requestId: ++quickActionSerial.current });
                  if (action !== 'cancel') closePatientQuickView(false);
                };
                const reschedule = (event: MouseEvent<HTMLButtonElement>) => {
                  rescheduleReturn.current = event.currentTarget;
                  setRescheduleRow(quickRow);
                };
                const quietIntake = intake.intakeAction !== null && primary !== 'intake';
                const savedDetailsAvailable = me?.patientMasterEnabled === true && quickRow.hasPatientRecord === true;
                const canBook = quickRow.hasPatientRecord === true && Boolean(quickRow.linkedPatientUmr) && day.scope === 'today' && !rangeEnd;
                return <div className="today-home__quick-actions" aria-label="Appointment actions">
                  {primary ? <AcButton size="sm" variant="primary"
                    data-testid={`home-quick-action-${primary}`} onClick={() => requestAction(primary)}>
                    {primary === 'intake' ? intake.intakeActionLabel : dayBoardActionLabel(primary, me?.role === 'receptionist')}
                  </AcButton> : null}
                  {futurePlanning && canReschedule ? <AcButton size="sm" variant={primary ? "ghost" : "primary"}
                    data-testid="home-quick-reschedule" onClick={reschedule}>Reschedule</AcButton> : null}
                  {savedDetailsAvailable ? <div className="today-home__quick-actions" aria-label="Patient history">
                    {(['visits', 'payments'] as const).map(detail => <AcButton key={detail}
                      size="sm" variant="ghost" data-testid={`home-quick-${detail}`} onClick={() => openQuickDetail(detail)}>
                      {QUICK_DETAIL_LABELS[detail]}</AcButton>)}
                  </div> : null}
                  {me?.role === 'receptionist' && day.scope !== 'future' && !rangeEnd ? (
                    <ReceptionistPaymentDoor row={quickRow} activeClinicId={activeClinicId}
                      onCollect={openCollectPanel}
                      collectDisabled={collectAppointmentId !== null || contactPanelOpen || utilityPanel !== null || visitLead !== null || recordOpen} />
                  ) : null}
                  {savedDetailsAvailable && canAct ? <AcButton size="sm" variant="ghost"
                    data-testid="home-quick-bill" onClick={() => openQuickDetail('bill')}>Itemised bill</AcButton> : null}
                  {canAct && canAssignDoctorOnRow(quickRow.status) && !receptionDoctorAssignmentLocked(quickRow) ? (
                    <AcButton size="sm" variant="ghost" data-testid="home-quick-assign-doctor"
                      onClick={() => requestAction('assign_doctor')}>Assign doctor</AcButton>
                  ) : null}
                  {secondary.includes('cancel') ? <AcButton size="sm" variant="ghost"
                    data-testid="home-quick-action-cancel" onClick={() => requestAction('cancel')}>
                    Cancel appointment</AcButton> : null}
                  {(savedDetailsAvailable || canBook || secondary.length > 0 || quietIntake || (canReschedule && !futurePlanning)) ? (
                    <details className="today-home__quick-more" data-testid="home-quick-more-actions">
                      <summary>Other actions</summary>
                      <div className="today-home__quick-actions">
                        {canReschedule && !futurePlanning ? <AcButton size="sm" variant="ghost"
                          data-testid="home-quick-reschedule" onClick={reschedule}>Reschedule</AcButton> : null}
                        {quietIntake ? <AcButton size="sm" variant="ghost" data-testid="home-quick-action-intake"
                          onClick={() => requestAction('intake')}>{intake.intakeActionLabel}</AcButton> : null}
                        {secondary.filter(action => action !== 'cancel').map(action => <AcButton key={action} size="sm" variant="ghost"
                          data-testid={`home-quick-action-${action}`} onClick={() => requestAction(action)}>
                          {dayBoardActionLabel(action, me?.role === 'receptionist')}
                        </AcButton>)}
                        {canBook ? (
                          <AcButton size="sm" variant="ghost" data-testid="home-quick-book" onClick={() => {
                            searchReturnQuickId.current = quickRow.id;
                            setHeroQuery(quickRow.linkedPatientUmr!);
                            setSearchOpen(true);
                          }}>Book appointment</AcButton>
                        ) : null}
                        {me?.patientMasterEnabled === true && quickRow.hasPatientRecord === true ? (
                          <div className="today-home__quick-actions" aria-label="Saved details">
                            {(['registration', ...(!canAct ? ['bill'] : []), 'estimate'] as QuickDetail[]).map(detail => <AcButton key={detail}
                              size="sm" variant="ghost" data-testid={`home-quick-${detail}`} onClick={() => openQuickDetail(detail)}>
                              {QUICK_DETAIL_LABELS[detail]}</AcButton>)}
                          </div>
                        ) : null}
                      </div>
                    </details>
                  ) : null}
                  {quickRow.hasPatientRecord === true && (quickRow.patientId || quickRow.linkedPatientUmr) ? (
                    <AcButton size="sm" variant="secondary" disabled={recordDoorBusy} onClick={() => void openPatientRecord(quickRow)} data-testid="home-quick-open-full-record">
                      {recordDoorBusy ? 'Opening...' : 'Open full record'}
                    </AcButton>
                  ) : null}
                </div>;
              })()}
              {collectionDoorError || recordDoorError ? <p role="alert">{collectionDoorError || recordDoorError}</p> : null}
            </section>
          )}
        </WorkPanel>
      ) : null}
      {recordOpen && !workSurfaceOpen && recordPatientId !== null ? (
        <WorkPanel title="Patient record" onClose={closePatientRecord} testId="home-patient-record-panel">
          {quickOpen && quickAppointmentId === recordAppointmentId ? <AcButton variant="ghost" size="sm" onClick={closePatientRecord} data-testid="home-record-back-quick">Quick view</AcButton> : null}
          <PatientRecordPage patientIdOverride={recordPatientId} selectedAppointmentId={recordAppointmentId} embedded />
        </WorkPanel>
      ) : null}
      {collectAppointmentId !== null ? (
        <PaymentDrawer
          inPlane
          appointmentId={collectAppointmentId}
          onClose={closeCollectDrawer}
          onRecorded={reloadAppointments}
          fullBillingHref={`/dashboard/billing-entry?appointmentId=${collectAppointmentId}${
            activeClinicId !== null ? `&clinicId=${activeClinicId}` : ''
          }`}
          dayBoardPath="/walkin"
          me={me}
        />
      ) : null}
      </DetailAside>
      </MasterDetail>
      {coverMode && activeClinicId!==null ? <ReceptionCoverPanel key={`${activeClinicId}:${coverMode}`} mode={coverMode}
        clinicId={activeClinicId} clinicName={activeClinicLabel??'Clinic'} date={day.date}
        doctors={appts.kind==='ready'?appts.clinicDoctors:[]}
        onClose={()=>{setCoverMode(null);requestAnimationFrame(()=>coverTrigger.current?.focus());}} onChanged={reloadAppointments}/> : null}
      </div>
      {/* FO-4/FO-5: the centered search-and-register modal. The typed query
          survives the modal closing (it lives on this page); registration
          drafts survive per D5 inside the modal's own model.

          RECEPTION-MONEY-TRUTH-V1 (B3 -- the C3 auto-refresh class, defect
          card 2026-08-12): registration + booking COMMIT inside this modal
          (register -> book today's slot -> a new board row exists), so
          closing it refetches the day board AND the workstation feed --
          the saved truth renders without a manual reload. One scoped day
          read + one feed read; a no-op close costs the same two bounded
          reads and stays honest. */}
      {searchOpen ? (
        <GlobalPatientSearchModal
          key={`${user?.id}:${me?.organizationId}:${me?.role}:${activeClinicId}:${me?.clinicIds.join(',')}:${me?.patientMasterEnabled}`}
          bookingUmr={searchReturnQuickId.current !== null ? heroQuery : undefined}
          initialQuery={heroQuery}
          // L-18: the desk home is the door -- the record it opens returns here.
          recordOrigin="walkin"
          onClose={() => {
            setSearchOpen(false);
            reloadAppointments();
            loadWorkstation();
            if (searchReturnQuickId.current === null) requestAnimationFrame(() => {
              document.querySelector<HTMLElement>('[data-testid="home-global-search-input"]')?.focus();
            });
          }}
          walkinClinicId={activeClinicId}
        />
      ) : null}
      </WorkspaceSurfaceContext.Provider>
    </main>
  );
}
